/*
Provides Interacting with Livepeers Smart Contracts
Exported variables should all be passed to the LivepeerStaker for it function properly
*/
import {
  useBondingManager,
  useLivepeerToken,
  useRoundsManager,
} from "../hooks/WithSmartContracts";
import { useState, useEffect } from "react";
import { ethers } from "ethers";

export default function useLivepeer() {
  // Import Livepeer's smart contracts
  const livepeerToken = useLivepeerToken();
  const bondingManager = useBondingManager();
  const roundsManager = useRoundsManager();
  // Current wallet connected through Metamask
  const [connectedWallet, setWallet] = useState("");
  // LPT the BondingManager is allowed to spend for the connected wallet
  const [allowance, setAllowance] = useState(0);
  // Current LPT round
  const [currentRound, setRound] = useState(0);
  // Current LPT balance of connected wallet
  const [LPTbalance, setLPTBalance] = useState(0);
  // Current ETH balance of connected wallet
  const [ETHbalance, setEthBalance] = useState(0);
  // Current Orchestrator the connected wallet is bonded with
  const [bondedWith, setBondedWith] = useState("");
  // Amount of LPT the connected wallet currently has bonded
  const [bondedLPT, setBondedLPT] = useState(0);
  // Whether the Delegator is in a pending/bonded/unbonded state
  // Locks the delegator out of Unbonding until the next round if set to 0 (if first Bonded in the current Round)
  const [delegatorState, setDelegatorState] = useState(0);
  // Amount Eth fees the connected wallet can withdraw
  const [pendingEth, setEth] = useState(0);
  // Progress of reading the transcoder table (which takes a while to retrieve)
  const [thisProgress, setProgress] = useState(0);
  // The sorted list of all active Orchestrators (on total stake)
  // Required to simulate how stake movements affect this order
  // to provide hints to the smart contract to reduce gast cost
  const [transcoderTable, setTranscoderTable] = useState(null);
  // If we have an error retrieving the sorted Transcoder table due to RPC errors
  // we want to display a popup telling them to refresh or just not worry about it
  const [isTranscoderTableErr, setTranscoderTableErr] = useState(false);
  const [isTranscoderTablePending, setTranscoderTablePending] = useState(false);
  // Next unbonding lock available for the connected wallet
  // Every lock from 0 up until this number is an LPT withdrawal
  // which can be still locked, available for withdrawal or already withdrawn
  const [nextUnbondingLock, setLock] = useState(0);
  // Unbonding lock info of the current connected wallet
  const [locks, setLocks] = useState(null);
  // Set to true during initial fetching, so we can display a `getting info` view
  const [isSyncing, setIsSyncing] = useState(false);

  // Simulates stake movement on the transcoderTable and returns associated hints
  function getHints(stake, from, to) {
    // Return null if the table is not available so we can call the function without hints
    if (!transcoderTable) {
      return null;
    }
    // Init hints to default values
    let hints = {
      oldDelegateNewPosPrev: ethers.constants.AddressZero,
      oldDelegateNewPosNext: ethers.constants.AddressZero,
      newDelegateNewPosPrev: ethers.constants.AddressZero,
      newDelegateNewPosNext: ethers.constants.AddressZero,
    };
    // Find from address if set, mutate stake and re-insert in list
    if (from != "") {
      let fromIdx = -1;
      for (let idx = 0; idx < transcoderTable.length; idx++) {
        if (transcoderTable[idx].address == from) {
          fromIdx = idx;
          break;
        }
      }
      // If we cannot make any simulations, no hints for the requestor
      if (fromIdx == -1) {
        console.log("Could not find " + from + " in the transcoder table");
        return null;
      } else {
        const thisTranscoder = transcoderTable.splice(fromIdx, 1)[0];
        thisTranscoder.stake -= stake;
        let inserted = false;
        for (let idx = 0; idx < transcoderTable.length; idx++) {
          if (transcoderTable[idx].stake < thisTranscoder.stake) {
            transcoderTable.splice(idx, 0, thisTranscoder);
            break;
          }
        }
        if (!inserted) {
          transcoderTable.push(thisTranscoder);
        }
      }
    }
    // Find to address, mutate stake and re insert in list
    let toIdx = -1;
    for (let idx = 0; idx < transcoderTable.length; idx++) {
      if (transcoderTable[idx].address == to) {
        toIdx = idx;
        break;
      }
    }
    if (toIdx == -1) {
      console.log("Could not find " + to + " in the transcoder table");
      return null;
    } else {
      const thisTranscoder = transcoderTable.splice(toIdx, 1)[0];
      thisTranscoder.stake += stake;
      let inserted = false;
      for (let idx = 0; idx < transcoderTable.length; idx++) {
        if (transcoderTable[idx].stake < thisTranscoder.stake) {
          transcoderTable.splice(idx, 0, thisTranscoder);
          break;
        }
      }
      if (!inserted) {
        transcoderTable.push(thisTranscoder);
      }
    }
    // Finally look up the new position of from and to and set their hints
    for (let idx = 0; idx < transcoderTable.length; idx++) {
      if (from != "" && transcoderTable[idx].address == from) {
        if (idx) {
          hints.oldDelegateNewPosPrev = transcoderTable[idx - 1].address;
        }
        if (idx != transcoderTable.length - 1) {
          hints.oldDelegateNewPosNext = transcoderTable[idx + 1].address;
        }
      }
      if (transcoderTable[idx].address == to) {
        if (idx) {
          hints.newDelegateNewPosPrev = transcoderTable[idx - 1].address;
        }
        if (idx != transcoderTable.length - 1) {
          hints.newDelegateNewPosNext = transcoderTable[idx + 1].address;
        }
      }
    }
    return hints;
  }

  // Asks the current user to switch over to or add the Arbitrum One network to Metamask
  async function checkArbitrum() {
    if (window.ethereum.networkVersion != 42161) {
      try {
        await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: ethers.utils.hexValue(42161) }],
        });
        return true;
      } catch (err) {
        // This error code indicates that the chain has not been added to MetaMask
        if (err.code === 4902) {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainName: "Arbitrum One",
                chainId: ethers.utils.hexValue(42161),
                nativeCurrency: { name: "ETH", decimals: 18, symbol: "ETH" },
                rpcUrls: ["https://arb1.arbitrum.io/rpc"],
              },
            ],
          });
          return window.ethereum.networkVersion == 42161;
        }
        return false;
      }
    }
    return true;
  }

  // Aks the user to connect an Eth address (or switch to Arb One network)
  async function connectWallet() {
    if (!window.ethereum) {
      alert("Get MetaMask!");
      return;
    }
    if (!(await checkArbitrum())) {
      return;
    }
    const accounts = await window.ethereum.request({
      method: "eth_requestAccounts",
    });
    if (accounts.length > 0) {
      const account = accounts[0];
      if (connectedWallet != account) {
        setWallet(account);
      }
    }
  }

  // Checks if we already have a connected wallet
  async function checkIfWalletIsConnected() {
    if (window.ethereum) {
      if (window.ethereum.networkVersion != 42161) {
        if (connectedWallet != "") {
          setWallet("");
        }
        return;
      }
      const accounts = await window.ethereum.request({
        method: "eth_accounts",
      });

      if (accounts.length > 0) {
        const account = accounts[0];
        if (connectedWallet != account) {
          setWallet(account);
        }
        return;
      }
    }
    if (connectedWallet != "") {
      setWallet("");
    }
  }

  // Get current sorted list of active Orchestrators
  async function getTranscoderTable() {
    try {
      if (!isTranscoderTablePending){
        setTranscoderTablePending(true);
        console.log("Retrieving list of Orchestrators...");
      }else{
        console.log("Retrieval of Orchestrators is still in progress...");
        return;
      }
      let sortedTable = [];
      const poolSize = await bondingManager.getTranscoderPoolSize();
      let transcoder = await bondingManager.getFirstTranscoderInPool();
      let bond = ethers.BigNumber.from(
        await bondingManager.transcoderTotalStake(transcoder)
      );
      let count = 1;
      while (transcoder != ethers.constants.AddressZero) {
        sortedTable.push({ address: transcoder.toLowerCase(), stake: bond });
        count++;
        let newProgress = Math.floor((count / poolSize) * 100);
        // So the bar moves at 5% increments
        if (newProgress % 5 == 0 && newProgress > thisProgress) {
          setProgress(newProgress);
        }
        transcoder = await bondingManager.getNextTranscoderInPool(transcoder);
        bond = await bondingManager.transcoderTotalStake(transcoder);
      }
      setTranscoderTable(sortedTable);
    } catch (error) {
      setTranscoderTableErr(true);
    }
    setTranscoderTablePending(false);
  }

  // Retrieves the current active Livepeer round
  async function getRound() {
    const thisRound = await roundsManager.currentRound();
    console.log("Current Livepeer round is #" + thisRound);
    if (thisRound != currentRound) {
      setRound(thisRound);
    }
  }

  // Get LPT balance of the connected wallet
  async function getLPTBalance() {
    const address = connectedWallet;
    const walletLPT = ethers.BigNumber.from(
      await livepeerToken.balanceOf(address)
    );
    console.log(
      "Found LPT balance of " +
        (walletLPT / ethers.constants.WeiPerEther).toFixed(2) +
        " LPT"
    );
    if (walletLPT != LPTbalance) {
      setLPTBalance(walletLPT);
    }
  }

  // Get ETH balance of the connected wallet
  async function getEthBalance() {
    const address = connectedWallet;
    const provider = new ethers.providers.Web3Provider(ethereum);
    const walletETH = ethers.BigNumber.from(await provider.getBalance(address));
    console.log(
      "Found ETH balance of " +
        (walletETH / ethers.constants.WeiPerEther).toFixed(4) +
        " ETH"
    );
    if (walletETH != ETHbalance) {
      setEthBalance(walletETH);
    }
  }

  // Get delegator info of the connected wallet
  async function getCurrentDelegation() {
    const address = connectedWallet;
    const { bondedAmount, delegateAddress, nextUnbondingLockId } =
      await bondingManager.getDelegator(address);
    const pendingStake = await bondingManager.pendingStake(address, 999999)
    console.log(
      "Is bonding " +
        (pendingStake / ethers.constants.WeiPerEther).toFixed(2) +
        " LPT at " +
        delegateAddress
    );
    if (delegateAddress != bondedWith) {
      setBondedWith(delegateAddress);
    }
    if (pendingStake != bondedLPT) {
      setBondedLPT(pendingStake);
    }
    if (nextUnbondingLockId != nextUnbondingLock) {
      setLock(nextUnbondingLockId);
    }
  }

  // Gets pending Eth fees of the connected wallet
  const getPendingFees = async () => {
    try {
      const thisPendingFees = ethers.BigNumber.from(
        await bondingManager.pendingFees(connectedWallet, 999999)
      );
      console.log(
        "Delegator account has " +
          (thisPendingFees / ethers.constants.WeiPerEther).toFixed(2) +
          " Eth fees available for withdrawal"
      );
      if (thisPendingFees != pendingEth) {
        setEth(thisPendingFees);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Get current approved amount of tokens of the connected wallet
  const getAllowance = async () => {
    try {
      const data = ethers.BigNumber.from(
        await livepeerToken.allowance(
          connectedWallet,
          "0x35Bcf3c30594191d53231E4FF333E8A770453e40"
        )
      );
      console.log(
        "BondingManager has an allowance of " +
          (data / ethers.constants.WeiPerEther).toFixed(2) +
          " LPT for this account"
      );
      if (data != allowance) {
        setAllowance(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Get all unbonding lock info of the connected wallet
  const getPendingLocks = async () => {
    try {
      let locks = [];
      for (let idx = 0; idx < nextUnbondingLock; idx++) {
        const unbondingLock = await bondingManager.getDelegatorUnbondingLock(
          connectedWallet,
          idx
        );
        locks.push({
          id: idx,
          amount: ethers.BigNumber.from(unbondingLock.amount),
          round: unbondingLock.withdrawRound,
        });
      }
      setLocks(locks);
    } catch (error) {
      console.log(error);
    }
  };

  // Get current delegator state of the connected wallet
  const getDelegatorState = async () => {
    try {
      const status = await bondingManager.delegatorStatus(connectedWallet);
      if (!status) {
        console.log("Account delegator is in a pending state");
      } else if (status == 1) {
        console.log("Account delegator is in a Bonded state");
      } else if (status == 2) {
        console.log("Account delegator is in a Unbonded state");
      } else {
        console.log("Account delegator is in an unknown state");
      }
      setDelegatorState(status);
    } catch (error) {
      console.log(error);
    }
  };

  // Calls all update functions for the connected wallet
  const updateDelegatorInfo = async () => {
    try {
      const { ethereum } = window;
      if (!ethereum || !connectedWallet || connectedWallet == "") {
        return;
      }
      console.log("Updating data for " + connectedWallet);
      await Promise.all([
        getLPTBalance(),
        getEthBalance(),
        getCurrentDelegation(),
        getRound(),
        getAllowance(),
        getDelegatorState(),
        getPendingFees(),
      ]);
    } catch (error) {
      console.log(error);
    }
  };

  // Calls all update functions for the connected wallet (if not syncing already)
  const refreshWallet = async () => {
    // If already doing the initial sync, skip
    if (isSyncing) {
      return;
    }
    // Check if wallet address has changed
    await checkIfWalletIsConnected();
    // Else refresh in background
    setIsSyncing(true);
    await updateDelegatorInfo();
    setIsSyncing(false);
  };

  // Subscribes to events of changing chains or metamask accounts
  const subscribeToWalletChanges = async () => {
    const { ethereum } = window;
    if (!ethereum) {
      return;
    }
    ethereum.on("chainChanged", refreshWallet);
    ethereum.on("accountsChanged", refreshWallet);
  };

  // Check if we have a connected wallet first rendered rendered, so we can start fetching data in the background
  useEffect(() => {
    subscribeToWalletChanges();
    refreshWallet();
  }, []);

  // Each time the connected wallet changes, refresh data
  useEffect(() => {
    const { ethereum } = window;
    if (!ethereum || !connectedWallet || connectedWallet == "") {
      return;
    }
    // TODO: This is an async function, make sure the component does not get unloaded while busy...
    if (!transcoderTable) {
      getTranscoderTable();
    }
    // Update delegator info
    refreshWallet();
  }, [connectedWallet]);

  // Each time the unbonding lock changes, refresh unbonding lock info
  useEffect(() => {
    if (connectedWallet != "") {
      getPendingLocks();
    }
  }, [nextUnbondingLock]);

  return {
    allowance,
    currentRound,
    ETHbalance,
    LPTbalance,
    bondedWith,
    bondedLPT,
    thisProgress,
    transcoderTable,
    isTranscoderTableErr,
    refreshWallet,
    delegatorState,
    getHints,
    connectWallet,
    connectedWallet,
    locks,
    checkIfWalletIsConnected,
    pendingEth,
    isSyncing,
  };
}
